import { type AccessoryOrderEntry } from '@/hooks/useAccessoryOrderItems';
import { formatUgx } from '@/utils/vehicleFormat';

/** Entries that will actually be part of the request. */
export const orderableEntries = (entries: AccessoryOrderEntry[]) => entries.filter((e) => !e.isGone && e.accessory);

export function orderTotals(entries: AccessoryOrderEntry[]) {
  const parts = orderableEntries(entries);
  return {
    lines: parts.length,
    units: parts.reduce((n, e) => n + e.quantity, 0),
    grandTotal: parts.reduce((n, e) => n + (e.accessory?.priceUgx ?? 0) * e.quantity, 0),
    unpriced: parts.filter((e) => e.accessory?.priceUgx == null).length,
  };
}

const unitPrice = (e: AccessoryOrderEntry) => e.accessory?.priceUgx ?? null;

/** "2 × Name (Brand) @ UGX 250,000 = UGX 500,000" — one line per part, for the request and the chat. */
export function orderLineText(e: AccessoryOrderEntry): string {
  const price = unitPrice(e);
  const name = `${e.quantity} × ${e.accessory?.name ?? e.name}${e.accessory?.brand ? ` (${e.accessory.brand})` : ''}`;
  return price === null
    ? `${name} — price on request`
    : `${name} @ ${formatUgx(price)} = ${formatUgx(price * e.quantity)}`;
}

type Contact = { fullName?: string | undefined; phone?: string | undefined; message?: string | undefined };

/** The WhatsApp opener: who, what, how many, at what price, so a follow-up needs no second question. */
export function orderWhatsAppMessage(entries: AccessoryOrderEntry[], contact: Contact = {}): string {
  const parts = orderableEntries(entries);
  const { units, grandTotal, unpriced } = orderTotals(entries);
  const lines = [
    'Hello Kabex Motors, I would like to order these accessories:',
    '',
    ...parts.map((e, i) => `${i + 1}. ${orderLineText(e)}`),
    '',
    `*Total: ${units} ${units === 1 ? 'item' : 'items'}, ${formatUgx(grandTotal)}*` +
      (unpriced > 0 ? ` (+ ${unpriced} priced on request)` : ''),
  ];
  const name = contact.fullName?.trim();
  const phone = contact.phone?.trim();
  const note = contact.message?.trim();
  if (name || phone || note) lines.push('');
  if (name) lines.push(`Name: ${name}`);
  if (phone) lines.push(`Phone: ${phone}`);
  if (note) lines.push(`Notes: ${note}`);
  return lines.join('\n');
}
